// src/utils/hierarchyHelpers.js

/**
 * Extract the first available PublicView from hierarchy
 * @param {Object} hierarchy - Company hierarchy
 * @returns {Object|null} - { companyId, factoryId, ouId, ... } or null
 */
export function getFirstPublicView(hierarchy) {
    if (!hierarchy?.factories?.length) return null;

    for (const factory of hierarchy.factories) {
        if (!factory.ous?.length) continue;

        for (const ou of factory.ous) {
            const activePV = ou.publicviews?.find(
                pv => pv.state === 'active' && pv.machines?.length > 0
            );

            if (activePV) {
                return {
                    companyId: hierarchy.id,
                    companyName: hierarchy.name,
                    factoryId: factory.id,
                    factoryName: factory.name,
                    ouId: ou.id,
                    ouName: ou.name,
                    publicview: activePV
                };
            }
        }
    }

    return null;
}

/**
 * Resolve a specific OU's PublicView — same shape as getFirstPublicView.
 *
 * Used for "return me to the PublicView I had open". Returns null when that OU
 * is gone, belongs to another company, or has no PublicView left to render, so
 * callers can fall back to getFirstPublicView.
 *
 * Deliberately looser than getFirstPublicView: that one PICKS a default and
 * prefers `state === 'active'`, while this one only has to confirm the OU the
 * user already chose is still viewable. The PublicView MFE renders on
 * `machines.length > 0` alone (PublicView.jsx `activePublicview`) and ignores
 * `state`, so requiring it here rejected OUs the app renders fine.
 *
 * @param {Object} hierarchy - Company hierarchy
 * @param {string} ouId - Remembered OU id
 * @returns {Object|null} - { companyId, factoryId, ouId, ... } or null
 */
export function getPublicViewByOu(hierarchy, ouId) {
    if (!hierarchy?.factories?.length || !ouId) return null;

    for (const factory of hierarchy.factories) {
        if (!factory.ous?.length) continue;

        for (const ou of factory.ous) {
            if (String(ou.id) !== String(ouId)) continue;

            const renderablePV = ou.publicviews?.find(pv => pv.machines?.length > 0);

            if (!renderablePV) return null;

            return {
                companyId: hierarchy.id,
                companyName: hierarchy.name,
                factoryId: factory.id,
                factoryName: factory.name,
                ouId: ou.id,
                ouName: ou.name,
                publicview: renderablePV
            };
        }
    }

    return null;
}

/**
 * Extract the first available Machine from hierarchy
 * @param {Object} hierarchy - Company hierarchy
 * @returns {Object|null} - { companyId, factoryId, ouId, machine } or null
 */
export function getFirstMachine(hierarchy) {
    if (!hierarchy?.factories?.length) return null;

    for (const factory of hierarchy.factories) {
        if (!factory.ous?.length) continue;

        for (const ou of factory.ous) {
            const activeMachine = ou.machines?.find(m => m.state === 'active');

            if (activeMachine) {
                return {
                    companyId: hierarchy.id,
                    companyName: hierarchy.name,
                    factoryId: factory.id,
                    factoryName: factory.name,
                    ouId: ou.id,
                    ouName: ou.name,
                    machine: activeMachine
                };
            }
        }
    }

    return null;
}

/**
 * Resolve a specific Machine — same shape as getFirstMachine.
 *
 * Used for "return me to the Machine I had open". Returns null when that
 * machine is gone, inactive, or belongs to another company, so callers can
 * fall back to getFirstMachine.
 *
 * @param {Object} hierarchy - Company hierarchy
 * @param {string} machineId - Remembered machine id
 * @returns {Object|null} - { companyId, factoryId, ouId, machine } or null
 */
export function getMachineById(hierarchy, machineId) {
    if (!hierarchy?.factories?.length || !machineId) return null;

    for (const factory of hierarchy.factories) {
        if (!factory.ous?.length) continue;

        for (const ou of factory.ous) {
            const machine = ou.machines?.find(m => String(m.id) === String(machineId));

            if (!machine) continue;
            if (machine.state !== 'active') return null;

            return {
                companyId: hierarchy.id,
                companyName: hierarchy.name,
                factoryId: factory.id,
                factoryName: factory.name,
                ouId: ou.id,
                ouName: ou.name,
                machine
            };
        }
    }

    return null;
}
